import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Pencil, Save } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import {
  businessRegistrationsApi,
  BusinessRegistration,
  RegistrationPackagePrice,
  RegistrationStatus,
} from '../../api/business-registrations';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Drawer } from '../../components/ui/Drawer';
import { EmptyState } from '../../components/ui/EmptyState';
import { Input } from '../../components/ui/Input';
import { Pagination } from '../../components/ui/Pagination';
import { Spinner } from '../../components/ui/Spinner';

/**
 * CAC registrations customers have paid PayIQ to file.
 *
 * The queue for whoever actually submits them — which is why the drawer
 * shows the proposed names, nature of business, address and proprietor in
 * full. Those are the fields that go on the CAC form, and until the app was
 * wired up it collected every one of them into React state and threw them
 * away, then told the customer their filing had started.
 *
 * Pricing lives on this page too. The app shipped ₦25,000 and ₦85,000 as
 * constants, so moving either meant a release to both stores. A change here
 * reaches new filings only: an open one carries the fee it was quoted, and
 * repricing it after the fact would be changing the deal.
 */

const PACKAGE_LABEL: Record<string, string> = {
  business_name: 'Business name',
  limited_company: 'Limited company',
};

const STATUS_LABEL: Record<RegistrationStatus, string> = {
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid — not started',
  filing: 'With the CAC',
  registered: 'Registered',
  rejected: 'Refused',
  cancelled: 'Cancelled',
};

const STATUS_VARIANT: Record<
  RegistrationStatus,
  'neutral' | 'warning' | 'info' | 'success' | 'danger'
> = {
  awaiting_payment: 'neutral',
  // Paid and untouched is the one that costs money to ignore: PayIQ has
  // taken a fee and owes work.
  paid: 'warning',
  filing: 'info',
  registered: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
};

const naira = (kobo: string | null) =>
  kobo === null
    ? '—'
    : `₦${(Number(kobo) / 100).toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const dateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-NG', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };

const selectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

export const BusinessRegistrations: React.FC = () => {
  usePage('Business Registrations');
  const toast = useToast();
  const { can } = useAuth();

  const [rows, setRows] = useState<BusinessRegistration[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<RegistrationStatus | ''>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState<BusinessRegistration | null>(null);
  const [packages, setPackages] = useState<RegistrationPackagePrice[]>([]);
  const [priceDraft, setPriceDraft] = useState<Record<string, string>>({});
  const [savingPrice, setSavingPrice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await businessRegistrationsApi.list({
        status: status || undefined,
        search: search.trim() || undefined,
        page,
        limit: 25,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (err) {
      toast.error(err, 'Failed to load registrations.');
    } finally {
      setLoading(false);
    }
  }, [status, search, page, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void businessRegistrationsApi
      .packages()
      .then((p) => {
        setPackages(p);
        setPriceDraft(Object.fromEntries(p.map((x) => [x.key, String(x.fee)])));
      })
      .catch(() => {
        // The queue is the point of the page; a pricing panel that failed to
        // load shouldn't take it down.
      });
  }, []);

  async function savePrice(key: string) {
    const fee = Number(priceDraft[key]);
    if (!Number.isInteger(fee) || fee < 0) {
      toast.error(null, 'Set the fee in whole naira.');
      return;
    }
    try {
      setSavingPrice(key);
      const saved = await businessRegistrationsApi.setPrice(key as never, { fee });
      setPackages((prev) => prev.map((p) => (p.key === key ? saved : p)));
      toast.success(`${PACKAGE_LABEL[key]} is now ₦${fee.toLocaleString('en-NG')}.`);
    } catch (err) {
      toast.error(err, 'Could not save that price.');
    } finally {
      setSavingPrice(null);
    }
  }

  async function move(
    reg: BusinessRegistration,
    next: RegistrationStatus,
    // Not `Record<string, string>`: the documents a completed filing carries
    // are objects, and typing them away here is what would have let the
    // screen silently send none.
    extra: Partial<Parameters<typeof businessRegistrationsApi.updateStatus>[1]> = {},
  ) {
    try {
      const saved = await businessRegistrationsApi.updateStatus(reg.id, {
        status: next,
        ...extra,
      });
      setSelected(saved);
      toast.success(`Marked ${STATUS_LABEL[next].toLowerCase()}.`);
      void load();
    } catch (err) {
      toast.error(err, 'Could not update that filing.');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {can('business_registrations.pricing') && packages.length > 0 && (
        <Card padding={20}>
          <h3 style={{ margin: '0 0 6px', fontSize: 15, color: 'var(--neutral-900)' }}>
            What we charge
          </h3>
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 13,
              color: 'var(--neutral-600)',
              maxWidth: 680,
            }}
          >
            Applies to new filings only — one already open keeps the fee it was
            quoted, because repricing it after the fact would be changing the
            deal. These used to be constants in the app, so moving one meant a
            release to both stores.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {packages.map((p) => (
              <div
                key={p.key}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 10,
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                  padding: 14,
                }}
              >
                <div style={{ minWidth: 180 }}>
                  <Input
                    label={PACKAGE_LABEL[p.key]}
                    value={priceDraft[p.key] ?? ''}
                    onChange={(e) =>
                      setPriceDraft((d) => ({
                        ...d,
                        [p.key]: e.target.value.replace(/[^\d]/g, ''),
                      }))
                    }
                  />
                </div>
                <Button
                  variant="secondary"
                  disabled={savingPrice === p.key || priceDraft[p.key] === String(p.fee)}
                  onClick={() => void savePrice(p.key)}
                >
                  <Save size={14} style={{ marginRight: 6 }} />
                  {savingPrice === p.key ? 'Saving…' : 'Save'}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card padding={20}>
        <p
          style={{
            margin: '0 0 16px',
            fontSize: 13,
            color: 'var(--neutral-600)',
            maxWidth: 680,
          }}
        >
          Filings customers have paid us to submit. Open one to see everything
          the CAC form needs — both proposed names, nature of business,
          registered address and the proprietor.
        </p>

        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as RegistrationStatus | '');
              setPage(1);
            }}
            style={selectStyle}
          >
            <option value="">All statuses</option>
            {(Object.keys(STATUS_LABEL) as RegistrationStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <div style={{ minWidth: 260 }}>
            <Input
              placeholder="Name or registration number"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No registrations match this filter."
            icon={FileText}
          />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', minWidth: 880, borderCollapse: 'collapse', fontSize: 13 }}
              >
                <thead>
                  <tr style={{ background: 'var(--neutral-200)' }}>
                    {['Proposed name', 'Package', 'Fee', 'Status', 'Opened', ''].map((h) => (
                      <th key={h} style={th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
                          {r.approvedName ?? r.proposedName1}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          {r.rcNumber ?? `or ${r.proposedName2}`}
                        </div>
                      </td>
                      <td style={td}>{PACKAGE_LABEL[r.packageKey]}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{naira(r.feeKobo)}</td>
                      <td style={td}>
                        <Badge variant={STATUS_VARIANT[r.status]}>
                          {STATUS_LABEL[r.status]}
                        </Badge>
                      </td>
                      <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                        {dateTime(r.createdAt)}
                      </td>
                      <td style={td}>
                        <Button variant="ghost" onClick={() => setSelected(r)}>
                          Open
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={Math.max(1, Math.ceil(total / 25))}
              total={total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <RegistrationDrawer
        registration={selected}
        canManage={can('business_registrations.manage')}
        onClose={() => setSelected(null)}
        onMove={move}
        onUploadError={(err) => toast.error(err, 'Could not attach that file.')}
      />
    </div>
  );
};

function RegistrationDrawer({
  registration,
  canManage,
  onClose,
  onMove,
  onUploadError,
}: {
  registration: BusinessRegistration | null;
  canManage: boolean;
  onClose: () => void;
  onMove: (
    reg: BusinessRegistration,
    next: RegistrationStatus,
    // Not `Record<string, string>`: the documents a completed filing carries
    // are objects, and typing them away is what would let this screen
    // silently send none.
    extra?: Partial<Parameters<typeof businessRegistrationsApi.updateStatus>[1]>,
  ) => void;
  /** Reported by the page, which owns the toast. */
  onUploadError: (err: unknown) => void;
}) {
  const [rcNumber, setRcNumber] = useState('');
  const [approvedName, setApprovedName] = useState('');
  const [reason, setReason] = useState('');

  /**
   * What the filing produced.
   *
   * Uploaded one at a time and held here as URLs until the row is marked
   * registered — the status call carries the URLs, not the files. A
   * certificate is megabytes and marking a registration is a small write;
   * pushing files through it would make the action that matters most in this
   * flow the slowest and likeliest to time out, and a timeout there is a
   * customer told nothing about a filing that finished.
   */
  const [documents, setDocuments] = useState<{ name: string; url: string }[]>(
    [],
  );
  const [uploading, setUploading] = useState(false);

  /**
   * Read the file, store it, keep the URL.
   *
   * Base64 rather than multipart because that is the shape every other
   * document upload here already takes, so signed delivery and the size
   * ceiling come for free rather than being reimplemented on one screen.
   */
  async function attach(file: File) {
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Could not read that file.'));
        reader.readAsDataURL(file);
      });
      const saved = await businessRegistrationsApi.uploadDocument(
        registration!.id,
        file.name,
        base64,
      );
      setDocuments((prev) => [...prev, saved]);
    } catch (err) {
      onUploadError(err);
    } finally {
      setUploading(false);
    }
  }

  useEffect(() => {
    setDocuments(registration?.documents ?? []);
    setRcNumber(registration?.rcNumber ?? '');
    setApprovedName(registration?.approvedName ?? '');
    setReason(registration?.rejectionReason ?? '');
  }, [registration]);

  if (!registration) return null;
  const r = registration;

  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={r.approvedName ?? r.proposedName1}
      subtitle={PACKAGE_LABEL[r.packageKey]}
      width={620}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Section title="What goes on the form">
          <Field label="First choice" value={r.proposedName1} />
          <Field label="Second choice" value={r.proposedName2} />
          <Field label="Nature of business" value={r.natureOfBusiness} />
          <Field label="Registered address" value={r.registeredAddress} />
          <Field label="Proprietor" value={r.proprietorName} />
          <Field label="Email" value={r.proprietorEmail} />
          <Field label="Phone" value={r.proprietorPhone} />
        </Section>

        <Section title="Payment">
          <Field label="Quoted" value={naira(r.feeKobo)} />
          <Field label="Received" value={naira(r.amountPaidKobo)} />
          <Field label="Paid at" value={dateTime(r.paidAt)} />
        </Section>

        {(r.rcNumber || r.registeredOn || r.rejectionReason) && (
          <Section title="What the CAC came back with">
            <Field label="Approved name" value={r.approvedName} />
            <Field label="Registration number" value={r.rcNumber} />
            <Field label="Registered on" value={r.registeredOn} />
            <Field label="Reason refused" value={r.rejectionReason} />
          </Section>
        )}

        {canManage && (
          <Section title="Move it along">
            {!r.paidAt ? (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)' }}>
                {/* Paid is the only signal PayIQ was actually engaged. */}
                Nothing to do until the fee arrives.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Input
                  label="Approved name (often the second choice)"
                  value={approvedName}
                  onChange={(e) => setApprovedName(e.target.value)}
                />
                <Input
                  label="Registration number"
                  value={rcNumber}
                  onChange={(e) => setRcNumber(e.target.value)}
                />
                <Input
                  label="Reason, if the CAC refused it"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />

                {/*
                  The documents the customer paid for.
                
                  They go out attached to the email that announces the
                  registration, so this is the one chance to put them on the
                  row: it sends once, and a correction afterwards does not
                  re-send. Marking a filing registered with nothing here
                  emails somebody the news and none of the paperwork.
                */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--neutral-800)' }}>
                    Documents to send
                  </div>

                  {documents.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                      Nothing attached yet. These are emailed to the customer
                      when you mark this registered.
                    </div>
                  ) : (
                    documents.map((doc) => (
                      <div
                        key={doc.url}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                          fontSize: 13,
                          color: 'var(--neutral-800)',
                        }}
                      >
                        <a
                          href={doc.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: 'var(--blue-600)' }}
                        >
                          {doc.name}
                        </a>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            setDocuments((prev) =>
                              prev.filter((d) => d.url !== doc.url),
                            )
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))
                  )}

                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    disabled={uploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      // Cleared either way, so re-picking the same file after
                      // a failure still fires a change event.
                      e.target.value = '';
                      if (file) void attach(file);
                    }}
                    style={{ fontSize: 12 }}
                  />
                  {uploading && (
                    <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                      Uploading…
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Button
                    variant="secondary"
                    onClick={() => onMove(r, 'filing', { approvedName })}
                  >
                    <Pencil size={14} style={{ marginRight: 6 }} />
                    With the CAC
                  </Button>
                  <Button
                    onClick={() =>
                      onMove(r, 'registered', {
                        approvedName,
                        rcNumber,
                        registeredOn: new Date().toISOString().slice(0, 10),
                        documents,
                      })
                    }
                  >
                    Registered
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => onMove(r, 'rejected', { rejectionReason: reason })}
                  >
                    Refused
                  </Button>
                </div>
              </div>
            )}
          </Section>
        )}
      </div>
    </Drawer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h4
        style={{
          margin: 0,
          fontSize: 12,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: 'var(--neutral-500)',
        }}
      >
        {title}
      </h4>
      <div
        style={{
          border: '1px solid var(--neutral-300)',
          borderRadius: 'var(--radius-md)',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 13 }}>
      <span style={{ color: 'var(--neutral-600)' }}>{label}</span>
      <span style={{ color: 'var(--neutral-900)', textAlign: 'right', maxWidth: 340 }}>
        {value || '—'}
      </span>
    </div>
  );
}
